<script setup lang="ts">
import {computed, onMounted, PropType, ref} from 'vue'

import {useI18n} from 'vue-i18n'
import {ItemsResponse, ListResponse} from '/@src/models/api'
import {ContentStatusValue, RawVideo} from "/@src/models/content";
import useArticle from "/@src/composable/content/useArticle";
import useNotyf from "/@src/composable/useNotyf";
import * as NProgress from "nprogress";
import {parseDurationText} from "/@src/utils/index"
import {getFlowDateText} from '/@src/utils/date'
type VideoStyle = 'simple' | 'rich'
const { t } = useI18n()
const props = defineProps({
  data: {
    type: Object as PropType<Array<RawVideo>>,
  },
  page:{
    type: Number,
    default: 1,
  },
  loading: {
    type: Boolean,
    default: true,
  },
  pageUpdatePath: {
    type: Boolean,
    default: false,
  },
  pageLinkDisplay: {
    type: Number,
    default: 5,
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  totalCount:{
    type: Number,
    default: 1,
  },
  showEmptyPlaceholder: {
    type: Boolean,
    default: true,
  },
  isMultiSelect:{
    type: Boolean,
    default: false,
  }
})
const emit = defineEmits(['onPageChange','itemClick'])
const currentPage = computed(() => {
  if (props.page) {
    return props.page || 1
  } else {
    return 1
  }
})
const notify = useNotyf()
const placeholderData = [{}, {}, {}, {}, {}, {}, {}, {}]

/**
 *  推送组件
 */
const pageChange = (page: number) => {
  emit('onPageChange', page)
}
const handleItemClick = (item:RawVideo)=>{
  emit('itemClick',item)
}
</script>

<template>
  <!--List-->
  <div class="list-view list-view-v2">
    <div class="article-items-container">
      <!--List Empty Search Placeholder -->
      <V-PlaceholderPage
        :class="[
        (props.loading ||!props.data||
          (props.data&&props.data.length !== 0) ||
          !showEmptyPlaceholder) &&
          'is-hidden',
      ]"
        :title="t('error-search-content-empty-title')"
        :subtitle="t('error-search-content-empty-subtitle')"
        larger
      >
        <template #image>
          <img
            class="light-image"
            src="/@src/assets/illustrations/placeholders/search-2.svg"
            alt=""
          />
          <img
            class="dark-image"
            src="/@src/assets/illustrations/placeholders/search-2-dark.svg"
            alt=""
          />
        </template>
      </V-PlaceholderPage>

      <transition-group v-if="!props.loading" name="list-complete" tag="div">
        <div v-if="props.data">
          <div
            v-for="item in props.data"
            :key="item.id"
            @click="handleItemClick(item)"
            class="list-view-item"
          >
            <div class="title-container">
              <h3>
                <V-Tag
                  v-if="item.status === 'DELETED'"
                  class="tag"
                  color="light"
                  :label="t('filter-content-status-delete')"
                />
                <V-Tag
                  v-else-if="item.status === 'DRAFT'"
                  class="tag"
                  color="warning"
                  :label="t('filter-content-status-draft')"
                  outlined
                />
                {{ item.title }}</h3>
              <transition name="fade-fast">
                <i class="iconfont icon-check" v-if="item.select"></i>
              </transition>
            </div>
            <div class="meta">
              <V-Avatar
                v-if="item.author"
                :picture="item.author.avatar"
                style="margin-right: 5px"
                size="mini"
              />
              <p v-if="item.author" style="margin-right: 5px">
                {{ item.author.nickname }}
              </p>
              <p>{{ getFlowDateText(item.datetime||item.display_time) }}</p>
            </div>
          </div>
        </div>
      </transition-group>
      <transition-group v-else name="list-complete" tag="div">
        <div v-for="item in placeholderData" :key="item" class="list-view-item">
          <V-PlaceloadText :lines="2" width="100%" last-line-width="50%" />
          <div class="meta">
            <V-PlaceloadAvatar size="small" squared />
            <V-PlaceloadText style="margin-left: 8px" :lines="1" width="30%" />
          </div>
        </div>
      </transition-group>
    </div>
    <V-FlexPagination
      v-if="props.data && props.data.length > 0"
      :item-per-page="props.pageSize"
      :total-items="props.totalCount"
      :current-page="currentPage"
      :max-links-displayed="props.pageLinkDisplay"
      :update-path="props.pageUpdatePath"
      @onPageChange="pageChange"
    />
  </div>
</template>

<style lang="scss">
@import '../../../scss/abstracts/_variables.scss';
@import '../../../scss/abstracts/_mixins.scss';
.list-view-v2 {
  .article-items-container{
    display: block;
    .list-view-item {
      @include vuero-s-card();

      cursor: pointer;
      margin-bottom: 12px;
      padding: 12px;

      .title-container{
        display: flex;
        align-items: start;
        justify-content: space-between;
        min-height: 25px;
        h3{
          @include textLine(2);
        }
        i{
          color: var(--primary);
        }
      }

      &:hover {
        box-shadow: var(--light-box-shadow);
      }

      .meta {
        display: flex;
        margin-top: 8px;
        align-items: center;
      }

      .list-view-item-inner {
        display: flex;
        cursor: pointer;
        .cover-container {
          position: relative;
          .tag {
            margin-right: 5px;
            position: absolute;
            left: 5px;
            top: 10px;
          }
          img {
            min-height: 130px;
            max-height: 130px;
            min-width: 190px;
            max-width: 190px;
            object-fit: cover;
            border-radius: var(--radius);
          }
        }
        > img {
          display: block;
          min-height: 130px;
          max-height: 130px;
          min-width: 190px;
          max-width: 10px;
          object-fit: cover;
          border-radius: var(--radius);
          margin: auto 0;
        }

        .has-image {
          margin-left: 16px;
        }

        .meta-left {
          display: flex;
          flex-direction: column;
          min-width: 65%;
          max-width: 65%;
          .tag{
            margin-right: 5px;
            height: unset;
            line-height: unset;
            padding-top: 1px;
            padding-bottom: 1px;
          }
          .author {
            color: var(--dark-text);
            font-weight: 400;
          }
          .lnil {
            margin-right: 5px;
          }
          h3 {
            font-family: var(--font-alt);
            color: var(--dark-text);
            font-weight: 600;
            font-size: 1.1rem;
            line-height: 1.5;
            @include textLine(2);

            .rating {
              margin-left: 12px;

              i {
                position: relative;
                top: -2px;
                font-size: 12px;
                color: var(--fade-grey-dark-4);

                &.active {
                  color: #ffc43b;
                }
              }
            }
          }

          p {
            font-size: 0.95rem;
            color: var(--light-text);
            margin-top: 5px;
            svg {
              height: 12px;
              width: 12px;
            }
          }

          > span {
            display: flex;
            align-items: center;
            margin-top: 10px;
            font-family: var(--font);
            font-size: 0.9rem;
            color: var(--primary);

            .icon-separator {
              font-size: 5px;
              color: var(--light-text);
              padding: 0 10px;
            }
          }
          .time {
            font-size: 0.85rem;
            color: var(--light-text);
            font-weight: 500;
            .time-plan {
              font-size: 0.85rem;
              color: var(--h-red);
              font-weight: 500;
            }
          }
          .icon-list {
            margin-top: auto;
            display: flex;

            > span {
              display: flex;
              align-items: center;
              margin-right: 15px;

              span {
                font-size: 0.85rem;
                font-family: var(--font-alt);
                color: var(--dark-text);
              }

              i {
                font-size: 1.2rem;
                margin-right: 6px;
                color: var(--light-text);
              }
            }
          }
        }

        .meta-right {
          margin-left: auto;
          display: flex;
          align-items: center;
          justify-content: flex-end;

          .buttons {
            margin-bottom: 0;
            margin-right: 10px;
          }
        }
      }
    }
  }
  .article-items-container *{
    grid-column-start: 1;
    grid-row-start: 1;
  }

}

.is-dark {
  .list-view-v2 {
    .list-view-item {
      @include vuero-card--dark();

      .list-view-item-inner {
        .meta-left {
          h3 {
            color: var(--dark-dark-text) !important;

            .rating {
              i:not(.active) {
                color: var(--dark-sidebar-dark-2);
              }
            }
          }

          > span {
            color: var(--primary);
          }

          .icon-list {
            span {
              color: var(--dark-dark-text);
            }
            > span {
              span {
                color: var(--dark-dark-text);
              }
            }
          }
        }

        .meta-right {
          .buttons {
            .button {
              &:first-child {
                background: var(--dark-sidebar-light-2);
                border-color: var(--dark-sidebar-light-8);
                color: var(--dark-dark-text);
              }
            }
          }
        }
      }
    }
  }
}

@media only screen and (max-width: 767px) {
  .list-view-v2 {
    .article-items-container{
      .list-view-item {
        padding: 20px;

        .list-view-item-inner {
          flex-direction: column;

          > img {
            width: 100%;
            max-width: 100%;
            min-height: 160px;
            max-height: 160px;
            margin-bottom: 1rem;
          }

          .meta-left {
            margin-left: 0;
            max-width: 100%;

            > span {
              margin-bottom: 16px;
            }

            .icon-list {
              flex-wrap: wrap;

              > span {
                flex-direction: column;
                text-align: center;
                margin: 10px;
                width: calc(33.3%);

                i {
                  margin: 0;
                }
              }
            }
          }

          .meta-right {
            margin: 16px 0 0 0;

            .buttons {
              margin: 0;
              width: 100%;
              display: flex;
              justify-content: space-between;

              .button {
                width: 48%;
              }
            }
          }
        }
      }
    }
  }
}

@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
  .list-view-v2 {
    .article-items-container{
      .list-view-inner {
        display: flex;
        flex-wrap: wrap;

        .list-view-item {
          padding: 20px;
          margin: 10px;
          width: calc(50% - 20px);

          .list-view-item-inner {
            flex-direction: column;
            height: 100%;
            min-height: 450px;

            > img {
              width: 100%;
              max-width: 100%;
              min-height: 160px;
              max-height: 160px;
              margin-bottom: 1rem;
            }

            .meta-left {
              margin-left: 0;

              > span {
                margin-bottom: 16px;
              }

              .icon-list {
                flex-wrap: wrap;

                > span {
                  flex-direction: column;
                  text-align: center;
                  margin: 10px;
                  width: calc(33.3% - 20px);

                  i {
                    margin: 0;
                  }
                }
              }
            }

            .meta-right {
              margin: auto 0 0 0;
              display: flex;
              flex-direction: column;
              justify-content: flex-end;

              .buttons {
                margin: 16px 0 0 0;
                width: 100%;
                display: flex;
                justify-content: space-between;

                .button {
                  width: 48%;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
